import type {
  BrandInvoiceMode,
  ReceiptChannel,
  ReceiptCurrency,
  ReceiptInvoiceStatus,
  ReceiptOrderStatus,
} from '@nadoutong/contracts';
import { FINANCE_ATTACHMENT_MAX_BYTES, MAX_TAX_POINT_BPS } from '@nadoutong/contracts';
import { formatCnyCents, formatUsdCents } from '../components/formatters.ts';
import type { I18nKey } from '../i18n/index.ts';
import type { ChipTone } from '../ui/index.ts';

/**
 * Display + form helpers for the manual receipt console (epic #1031
 * E2-R5, #1063). Pure functions only — kept out of the page files so
 * the submit dialog, the queue, and the detail drawer agree on money
 * rendering and the base64 evidence encoding.
 */

export const RECEIPT_STATUS_LABEL_KEYS: Record<ReceiptOrderStatus, I18nKey> = {
  submitted: 'receipts.status.submitted',
  credited: 'receipts.status.credited',
  rejected: 'receipts.status.rejected',
  voided: 'receipts.status.voided',
};

export const RECEIPT_CHANNEL_LABEL_KEYS: Record<ReceiptChannel, I18nKey> = {
  bank: 'receipts.channel.bank',
  alipay: 'receipts.channel.alipay',
  wechat: 'receipts.channel.wechat',
  usdt: 'receipts.channel.usdt',
  other: 'receipts.channel.other',
};

export function receiptStatusChipTone(status: ReceiptOrderStatus): ChipTone {
  if (status === 'credited') return 'ok';
  if (status === 'rejected') return 'bad';
  if (status === 'submitted') return 'warn';
  return 'neutral';
}

export const RECEIPT_INVOICE_STATUS_LABEL_KEYS: Record<ReceiptInvoiceStatus, I18nKey> = {
  none: 'receipts.invoice.status.none',
  open: 'receipts.invoice.status.open',
  issued: 'receipts.invoice.status.issued',
  voided: 'receipts.invoice.status.voided',
};

export const BRAND_INVOICE_MODE_LABEL_KEYS: Record<BrandInvoiceMode, I18nKey> = {
  'cn-tax': 'receipts.invoice.mode.cn_tax',
  commercial: 'receipts.invoice.mode.commercial',
};

export function receiptInvoiceStatusChipTone(status: ReceiptInvoiceStatus): ChipTone {
  if (status === 'issued') return 'ok';
  if (status === 'open') return 'warn';
  return 'neutral';
}

/**
 * Render basis points as a human percentage ("600" → "6%", "650" →
 * "6.5%", "667" → "6.67%"). Integer math only — no float drift.
 */
export function formatTaxPointBps(bps: number): string {
  const whole = Math.floor(bps / 100);
  const frac = bps % 100;
  if (frac === 0) return `${whole}%`;
  if (frac % 10 === 0) return `${whole}.${frac / 10}%`;
  return `${whole}.${String(frac).padStart(2, '0')}%`;
}

export type ParseTaxPointResult = { ok: true; bps: number } | { ok: false };

/**
 * Parse an operator-typed tax point percentage ("6", "6.5", "0.75")
 * into integer basis points — same string-decimal discipline as
 * `parseAmountInputToMinorUnits` (never `value * 100` on a float). Bounded to
 * [0, MAX_TAX_POINT_BPS]; the brand cap is enforced server-side.
 */
export function parseTaxPointInputToBps(raw: string): ParseTaxPointResult {
  const trimmed = raw.trim();
  if (!/^(?:\d+|\d+\.\d{1,2}|\.\d{1,2})$/.test(trimmed)) {
    return { ok: false };
  }
  const [wholeRaw = '', fracRaw = ''] = trimmed.split('.');
  const whole = wholeRaw.length === 0 ? 0 : Number(wholeRaw);
  const frac = Number(fracRaw.padEnd(2, '0'));
  if (!Number.isSafeInteger(whole) || !Number.isSafeInteger(frac)) {
    return { ok: false };
  }
  const bps = whole * 100 + frac;
  if (!Number.isSafeInteger(bps) || bps < 0 || bps > MAX_TAX_POINT_BPS) {
    return { ok: false };
  }
  return { ok: true, bps };
}

/**
 * Format integer minor units in the RECEIPT currency (CNY cents / USD
 * cents) — receipts are the one ops surface where the primary money
 * fact is not USD-pivoted.
 */
export function formatReceiptMinor(amountMinor: number, currency: ReceiptCurrency): string {
  return currency === 'CNY' ? formatCnyCents(amountMinor, false) : formatUsdCents(amountMinor, false);
}

/** Idempotency key for one submit-dialog session. */
export function randomClientRef(): string {
  if (globalThis.crypto !== undefined && 'randomUUID' in globalThis.crypto) {
    return globalThis.crypto.randomUUID();
  }
  return `rcpt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Human copy for the upload cap ("5 MB" from the contract constant). */
export function attachmentMaxMbLabel(): string {
  return String(Math.round(FINANCE_ATTACHMENT_MAX_BYTES / (1024 * 1024)));
}

export function isOversizeAttachment(sizeBytes: number): boolean {
  return sizeBytes > FINANCE_ATTACHMENT_MAX_BYTES;
}

/**
 * Encode file bytes as standard base64 for the JSON upload path.
 * Chunked so large (≤ 5 MB) files do not blow the argument-spread
 * limit of `String.fromCharCode`.
 */
export function bytesToBase64(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.subarray(i, i + CHUNK);
    let part = '';
    for (const byte of slice) {
      part += String.fromCharCode(byte);
    }
    binary += part;
  }
  return btoa(binary);
}

/** Decode a base64 download payload back into bytes for an object URL. */
export function base64ToBytes(dataBase64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Hand a decoded attachment payload to the browser as a file (#1957).
 *
 * Extracted because the same nine lines — decode, wrap in a Blob typed from the
 * payload, mint an object URL, click a synthetic anchor, revoke — were written
 * out per call site, and the revoke is the line that quietly leaks when it is
 * copied slightly wrong. Callers keep their own error handling: what "download
 * failed" should say differs per surface, and the payload's own metadata
 * (`content_type`, `file_name`) is what names the file, never the caller.
 */
export function downloadAttachmentPayload(payload: {
  readonly attachment: { readonly content_type: string; readonly file_name: string };
  readonly data_base64: string;
}): void {
  const bytes = base64ToBytes(payload.data_base64);
  const blob = new Blob([bytes], { type: payload.attachment.content_type });
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = payload.attachment.file_name;
    link.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}
